import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { IconKey } from '@common/classes/icons';
import {
    AdministrationPermissions,
    CodeListPermissions,
    ContractPermissions,
    CustomerPermissions,
    FaqPermissions,
    InvoicePermissions,
    NoticeBoardPermissions,
    PricelistPermissions,
    ReportingPermissions,
    TicketPermissions,
    UtilitiesConsumptionPermissions,
    UtilitiesPriceCalculationPermissions,
    VesselPermissions,
    VesselVisitPermissions
} from '@common/classes/permissions';
import { PermissionType } from '@common/models/user';
import { PropertyFunction } from '@common/models/util.interface';
import { SVGIcon } from '@progress/kendo-svg-icons';

export interface NavItem extends PanelBarItemModel {
    readonly permissions?: PermissionType | PermissionType[];
}

export const NAVIGATION: NavItem[] = [
    // {
    //     title: _('Dashboard'),
    //     icon: 'faSolidInbox',
    //     path: '/home',
    //     id: 'dashboard',
    //     selected: true,
    //     content: 'Tets'
    // },
    {
        title: _('Ticketing'),
        icon: 'faSolidTicket',
        id: 'ticketing',
        permissions: [TicketPermissions],
        children: [
            {
                title: _('Tickets'),
                icon: 'faSolidTicket',
                path: '/ticket',
                id: 'ticket',
                permissions: [TicketPermissions.View]
            }
        ]
    },
    {
        title: _('Vessels'),
        icon: 'faSolidShip',
        id: 'vessel',
        permissions: [VesselVisitPermissions],
        children: [
            {
                title: _('PCS Visits'),
                icon: 'faSolidLocationDot',
                path: '/vessel-visit/pcs',
                id: 'pcsVesselVisits',
                permissions: [VesselVisitPermissions.View]
            },
            {
                title: _('RT Visits'),
                icon: 'faSolidLocationDot',
                path: '/vessel-visit/rt',
                id: 'rtVesselVisits',
                permissions: [VesselVisitPermissions.View]
            },
            {
                title: _('Registry'),
                icon: 'faSolidBoxArchive',
                path: '/vessel',
                id: 'vessels',
                permissions: [VesselPermissions.View]
            }
        ]
    },
    {
        title: _('Invoice'),
        icon: 'bootstrapEnvelopePaperFill',
        id: 'invoice',
        permissions: [InvoicePermissions],
        children: [
            {
                title: _('Create Invoice'),
                icon: 'faSolidPlus',
                path: '/invoice/create',
                id: 'createInvoice',
                permissions: [InvoicePermissions.Action.Create]
            },
            {
                title: _('Invoice List'),
                icon: 'faSolidList',
                path: '/invoice',
                id: 'invoiceList',
                permissions: [InvoicePermissions.View]
            }
        ]
    },
    {
        title: _('Commercial System'),
        icon: 'faSolidCalculator',
        id: 'comercialSystem',
        permissions: [PricelistPermissions, ContractPermissions, CustomerPermissions],
        children: [
            {
                title: _('Pricelists'),
                icon: 'faSolidFileLines',
                path: '/pricelist',
                id: 'pricelistList',
                permissions: [PricelistPermissions.View]
            },
            {
                title: _('Contracts'),
                icon: 'faSolidFileContract',
                path: '/contract',
                id: 'contractList',
                permissions: [ContractPermissions.View]
            },
            {
                title: _('Customers'),
                icon: 'faSolidBriefcase',
                path: '/customer',
                id: 'customerList',
                permissions: [CustomerPermissions.View]
            }
        ]
    },
    {
        title: _('Utilities'),
        icon: 'faSolidBoltLightning',
        id: 'utilities',
        permissions: [UtilitiesConsumptionPermissions, UtilitiesPriceCalculationPermissions],
        children: [
            {
                title: _('Consumption Overview'),
                icon: 'faSolidBoltLightning',
                path: '/utilities-consumption',
                id: 'consumptionOverviewList',
                permissions: [UtilitiesConsumptionPermissions.View]
            },
            {
                title: _('Calculation Overview'),
                icon: 'faSolidCalculator',
                path: '/utilities-calculation',
                id: 'calculationOverviewList',
                permissions: [UtilitiesPriceCalculationPermissions.View]
            }
        ]
    },
    {
        title: _('Reports'),
        icon: 'faSolidChartColumn',
        path: '/reports',
        id: 'reports',
        permissions: [ReportingPermissions.View.List]
    },
    {
        title: _('Notifications'),
        icon: 'faSolidBell',
        path: '/notice-board/notification',
        id: 'notifications-userlist',
        permissions: [NoticeBoardPermissions.View]
    },
    {
        title: _('FAQ'),
        icon: 'faSolidCircleQuestion',
        path: '/faq',
        id: 'faq',
        permissions: [FaqPermissions.View]
    },
    {
        title: _('Administration'),
        icon: 'faSolidGear',
        id: 'administration',
        permissions: [AdministrationPermissions, CodeListPermissions, FaqPermissions],
        children: [
            {
                title: _('Users'),
                icon: 'faSolidUsers',
                path: '/administration/user',
                id: 'adminUsers',
                permissions: [AdministrationPermissions.User]
            },
            {
                title: _('Organizations'),
                icon: 'faSolidBuilding',
                path: '/administration/organization',
                id: 'adminOrganizations',
                permissions: [AdministrationPermissions.Organization]
            },
            {
                title: _('Roles'),
                icon: 'faSolidKey',
                path: '/administration/role',
                id: 'adminRoles',
                permissions: [AdministrationPermissions.Role]
            },
            {
                title: _('Codelists'),
                icon: 'faSolidList',
                path: '/administration/codelists',
                id: 'codelists',
                permissions: [CodeListPermissions.View]
            },
            {
                title: _('Application Settings'),
                icon: 'faSolidGear',
                path: '/administration/application-setting',
                id: 'application-settings',
                permissions: [AdministrationPermissions.Settings.View]
            },
            {
                title: _('Faq Categories'),
                icon: 'faSolidTags',
                path: '/administration/faq/categories',
                id: 'faq-categories',
                permissions: [FaqPermissions.View]
            }
        ]
    }
];

const flatten = (members: NavItem[]): NavItem[] => {
    let children = [];

    return members
        .map((m) => {
            if (m.children && m.children.length) {
                children = [...children, ...m.children];
            }
            return m;
        })
        .concat(children.length ? flatten(children) : children);
};

export const NAVIGATION_FLAT = flatten(NAVIGATION);

export interface PanelBarItemModel {
    // Sets the title of the PanelBar item.
    title: string;

    // Allows the component to set the `"id"` property of each item.
    id: string;

    //Defines the icon that will be rendered next to the title.
    icon: IconKey;

    // Defines the icon that will be rendered next to the title by using a custom CSS class.
    iconClass?: string;

    // Defines an SVGIcon to be rendered.
    // The input can take either an [existing Kendo SVG icon](slug:svgicon_list) or a custom one.
    svgIcon?: SVGIcon;

    // Defines the location of the image that will be displayed next to the title.
    imageUrl?: string;

    // When set to `true`, disables a PanelBar item.
    disabled?: boolean;

    // When set to `true`, expands the PanelBar item.
    expanded?: boolean;

    // Sets the focused state of a PanelBar item.
    focused?: boolean;

    // Sets the selected state of a PanelBar item.
    selected?: boolean;

    // Specifies if the item will be hidden.
    hidden?: boolean | PropertyFunction<boolean>;

    // Defines the child items as an array.
    children?: PanelBarItemModel[];

    // Sets the content of the PanelBar item.
    content?: any;

    // Sets permissions for the PanelBar item.
    readonly permissions?: PermissionType | PermissionType[];

    // Sets the path of the PanelBar item.
    path?: string;
}
